import {
  Avatar,
  Button,
  Menu,
  MenuItem,
  isWidthUp,
  withWidth,
  WithWidthProps,
} from "@material-ui/core";
import AppBar from "@material-ui/core/AppBar";
import CssBaseline from "@material-ui/core/CssBaseline";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import List from "@material-ui/core/List";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import KeyboardArrowDown from "@material-ui/icons/KeyboardArrowDown";
import Person from "@material-ui/icons/PersonRounded";
import MenuIcon from "@material-ui/icons/Menu";
import clsx from "clsx";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RoleEnum } from "../../interfaces";
import { userActions, userSelectors } from "../../redux";
import { logout } from "../../services/firebase";
import links from "../AppDrawer/links";
import ListItemLink from "../ListItemLink";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    // minWidth: '400px',
  },
  appBar: {
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  hide: {
    display: "none",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  title: {
    flexGrow: 1,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
    marginTop: theme.spacing(5),
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    transform: `translate(0, 0)`,
  },
  contentShift: {
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    transform: `translate(${drawerWidth}px, 0)`,
  },
  avatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
  },
}));

interface AppDrawerProps extends WithWidthProps {
  role: RoleEnum;
  title: string;
}

const AppDrawer: React.FC<Partial<AppDrawerProps>> = ({
  children,
  title,
  role,
  width,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = React.useState(width ? isWidthUp("md", width) : true);
  const [[anchorEl, currentMenuId], setCurrentMenuId] = React.useState<
    [HTMLElement | null, string | null]
  >([null, null]);
  const user = useSelector(userSelectors.getUser);
  const dispatch = useDispatch();
  const handleMenuOpen = (id: string) => (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setCurrentMenuId([event.currentTarget, id]);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleMenuClose = () => {
    setCurrentMenuId([null, null]);
  };

  const handleLogout = () => {
    logout(() => dispatch(userActions.clearUser()), handleMenuClose);
  };

  const isMenuOpen = (id: string) => Boolean(anchorEl) && currentMenuId === id;
  const menuId = "account-menu";
  const roleMenuId = "role-menu";

  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={menuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen(menuId)}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleLogout}>Logout</MenuItem>
    </Menu>
  );

  const renderRoleMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id={roleMenuId}
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMenuOpen(roleMenuId)}
      onClose={handleMenuClose}
    >
      {user?.roles?.map((item) => (
        <MenuItem key={item} onClick={handleLogout}>
          {item}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <div className={classes.root}>
      <CssBaseline />
      <AppBar
        position="fixed"
        className={clsx(classes.appBar, {
          [classes.appBarShift]: open,
        })}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className={clsx(classes.menuButton, open && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
          <Typography className={classes.title} variant="h6" noWrap>
            {title}
          </Typography>

          {user && (
            <>
              <Button
                style={{ color: "white", fontWeight: "bolder" }}
                onClick={
                  user.roles && user.roles?.length > 1
                    ? handleMenuOpen(roleMenuId)
                    : undefined
                }
                color="default"
                endIcon={
                  user.roles &&
                  user.roles.length > 1 && (
                    <KeyboardArrowDown></KeyboardArrowDown>
                  )
                }
              >
                {role}
              </Button>

              <IconButton
                edge="end"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenuOpen(menuId)}
                color="inherit"
              >
                {user.photoURL ? (
                  <Avatar
                    sizes={classes.avatar}
                    alt=""
                    src={user.photoURL}
                  ></Avatar>
                ) : (
                  <Person />
                )}
              </IconButton>
            </>
          )}
        </Toolbar>
      </AppBar>
      {renderMenu}
      {renderRoleMenu}
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={open}
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.drawerHeader}>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </div>
        <Divider />
        <List>
          {links[role || "default"].map(({ text, path, icon }) => (
            <ListItemLink
              text={text}
              key={path}
              to={path}
              icon={icon}
              onClick={
                width && !isWidthUp("md", width) ? handleDrawerClose : undefined
              }
            ></ListItemLink>
          ))}
        </List>
        <Divider />
      </Drawer>
      <main
        className={clsx(classes.content, {
          [classes.contentShift]: open,
        })}
      >
        {children}
      </main>
    </div>
  );
};

export default withWidth()(AppDrawer);
